<template>
  <NuxtLayout>
    <div class="forgottenPassword container">
      <template v-if="!passwordChanged">
        <div class="customError__Wrapper">
          <div class="customError" v-if="passwordReuse">
            <p v-text="$t('loyaltyErrors.passwordReuse')" />
          </div>
          <div class="customError" v-if="activationTokenInvalid">
            <p v-text="$t('loyaltyErrors.activationTokenInvalid')" />
          </div>
          <div v-if="customError && Object.keys(customError).length" class="customError">
            <p v-text="$t(`loyaltyErrors.error${ customError.code }`)" />
          </div>
        </div>
        <h1 v-text="$t('forgottenPassword.createNewTitle')" />
        <form @submit.prevent="submitForm" class="absoluteLabelsForm forgottenPassword__Form">
          <TextField :ref="addField" class="form__PasswordField form__PasswordFirstField" v-model="form.password" :label="$t('forms.password')" type="password" :info="$t('forms.passwordNotice')" :validate="{type: ['required', 'password'], message: { required: $t('formErrors.required'), password: $t('formErrors.password') }}" :errors="formErrors?.field_errors?.password">
            <template #append>
              <button type="button" @click="togglePasswordVisibility">
                <Icon name="password" />
                <Icon name="password-hide" />
              </button>
            </template>
          </TextField>
          <TextField :ref="addField" class="form__PasswordField form__PasswordRepeatField" v-model="form.confirm_password" :label="$t('forms.passwordRepeat')" type="password" :validate="{type: ['required', 'sameAs'], compareValue: form.password, message: { required: $t('formErrors.required'), sameAs: $t('formErrors.sameAsPassword') }}" :errors="formErrors?.field_errors?.confirm_password">
            <template #append>
              <button type="button" @click="togglePasswordVisibility($event, repeat = true)">
                <Icon name="password" />
                <Icon name="password-hide" />
              </button>
            </template>
          </TextField>
          <Button class="forgottenPassword__FormButton" medium full-width :disabled="dataFetchInProgress">{{ $t('forgottenPassword.submit') }}</Button>
        </form>
      </template>
      <template v-else>
        <div class="forgottenPassword__Message">
          <Icon name="check-circle-fill"/>
          <h2>{{ $t('forgottenPassword.passwordChanged') }}</h2>
          <Button type="nuxt-link" :to="link('Loyalty')" class="forgottenPassword__FormButton" medium full-width>{{ $t('close') }}</Button>
        </div>
      </template>
    </div>
  </NuxtLayout>
</template>

<script setup>
  defineOptions({
    auth: 'aminess'
  });

  const props = defineProps({
    page: {
      type: Object
    }
  })

  const { t } = useI18n();
  const { link } = useSetup();
  const route = useRoute();

  const dataFetchInProgress = ref(false);
  const passwordChanged = ref(false);
  const operationToken = ref('');
  const passwordReuse = ref(false);
  const activationTokenInvalid = ref(false);
  const formErrors = ref(null);
  const { customError, setCustomError } = useCustomFormErrors(formErrors.value);
  const form = ref({
    password: '',
    confirm_password: '',
  });
  const field = ref([]);

  if (route?.query?.operation_token) {
    operationToken.value = route.query.operation_token;
  }

  async function submitForm() {
    dataFetchInProgress.value = true;
    passwordReuse.value = false;
    activationTokenInvalid.value = false;
    let fieldsForValidation = [...field.value];
    let isValid = useFormIsValid(fieldsForValidation);
    if(!isValid) {
      setTimeout(() => {
        let element = document.querySelector('.forgottenPassword__Form .isError');
        if(element) element.scrollIntoView({ behavior: 'smooth' });
      }, 1);
    }
    else {
      try {
        let body = JSON.parse(JSON.stringify(form.value));
        for (let [key, value] of Object.entries(body)) {
          if (value === '') {
            body[key] = null;
          }
        }
        if (operationToken.value) {
          body.operation_token = operationToken.value;
        }
        await useAuthFetch(`loyalty/confirm/`, {
          method: 'POST',
          strategy: 'aminess',
          body: body,
          headers: {
            'Application-Origin': 'SHAPE-WEB'
          }
        });
        passwordChanged.value = true;
      } catch(error) {
        console.log(error)
        if (error.data) formErrors.value = error.data;

        if (error.response?._data?.field_errors && error.response?._data?.field_errors['loyalty.password_reuse']) {
          passwordReuse.value = true;
        }

        if (error.response?._data?.field_errors && error.response?._data?.field_errors['loyalty.passwords_do_not_match']) {
          formErrors.value.field_errors = {
            password: 107,
            confirm_password: 107,
          };
        }

        if (error.response?._data?.errors && error.response?._data?.errors['profile.activation_token_invalid']) {
          activationTokenInvalid.value = true;
        }

        setCustomError(formErrors.value);
      }
    }
    dataFetchInProgress.value = false;
  }

  function addField(el) {
    if(el && !el?.added) {
      field.value.push(el);
      el.added = true
    }
  }
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_forgottenPassword.scss');
</style>